import { useMemo, useState, type FormEvent } from "react";
import { ModalShell } from "@/components/ui/ModalShell";
import { FormField, MessageBanner, ModalActions, SelectInput, TextInput } from "@/components/ui/primitives";
import type { Account } from "@/hooks/useAccounts";
import type { Category } from "@/hooks/useCategories";
import type { Tag } from "@/hooks/useTags";
import type { Installment } from "@/hooks/useInstallments";
import type { SavingPlanBalance } from "@/hooks/useSavings";
import type { TransactionType } from "@/hooks/useTransactions";
import type { SavingPocket } from "@/hooks/useSavingPockets";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "@/config/featureFlags";

export interface BulkUpdateTransactionData {
  category_id?: number;
  tag_id?: number;
  type_id?: number;
  transaction_date?: string;
  installment_id?: number;
  saving_plan_id?: number;
  saving_pocket_id?: number;
  account_id?: number;
}

interface BulkUpdateTransactionsModalProps {
  isOpen: boolean;
  selectedCount: number;
  accounts: Account[];
  categories: Category[];
  tags: Tag[];
  installments: Installment[];
  savingPlans: SavingPlanBalance[];
  savingPockets: SavingPocket[];
  transactionTypes: TransactionType[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: BulkUpdateTransactionData) => Promise<void>;
}

interface BulkFormState {
  category_id: string;
  tag_id: string;
  type_id: string;
  transaction_date: string;
  installment_id: string;
  saving_plan_id: string;
  saving_pocket_id: string;
  account_id: string;
}

const initialFormState: BulkFormState = {
  category_id: "",
  tag_id: "",
  type_id: "",
  transaction_date: "",
  installment_id: "",
  saving_plan_id: "",
  saving_pocket_id: "",
  account_id: "",
};

export const BulkUpdateTransactionsModal = ({
  isOpen,
  selectedCount,
  accounts,
  categories,
  tags,
  installments,
  savingPlans,
  savingPockets,
  transactionTypes,
  isSubmitting,
  onClose,
  onSubmit,
}: BulkUpdateTransactionsModalProps) => {
  const { t } = useTranslation(["common", "transactions"]);
  const [formData, setFormData] = useState<BulkFormState>(initialFormState);
  const [localError, setLocalError] = useState<string | null>(null);

  const hasChanges = useMemo(() => {
    return Object.values(formData).some((value) => value !== "");
  }, [formData]);
  const sortedAccounts = useMemo(
    () => [...accounts].sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );
  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const sortedTags = useMemo(
    () => [...tags].sort((a, b) => a.name.localeCompare(b.name)),
    [tags],
  );
  const sortedInstallments = useMemo(
    () => [...installments].sort((a, b) => a.name.localeCompare(b.name)),
    [installments],
  );
  const sortedSavingPlans = useMemo(
    () => [...savingPlans].sort((a, b) => a.name.localeCompare(b.name)),
    [savingPlans],
  );
  const sortedSavingPocketOptions = useMemo(
    () => [...savingPockets].sort((a, b) => a.name.localeCompare(b.name)),
    [savingPockets],
  );

  const handleFieldChange = (field: keyof BulkFormState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setLocalError(null);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const parseOptionalNumber = (value: string): number | undefined => {
    if (!value) return undefined;
    return Number(value);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasChanges) {
      setLocalError(t("transactions.selectOneField", { defaultValue: "Select at least one field to update." }));
      return;
    }

    setLocalError(null);
    try {
      await onSubmit({
        category_id: parseOptionalNumber(formData.category_id),
        tag_id: parseOptionalNumber(formData.tag_id),
        type_id: parseOptionalNumber(formData.type_id),
        transaction_date: formData.transaction_date || undefined,
        installment_id: parseOptionalNumber(formData.installment_id),
        saving_plan_id: parseOptionalNumber(formData.saving_plan_id),
        saving_pocket_id: FEATURE_FLAGS.savingPockets ? parseOptionalNumber(formData.saving_pocket_id) : undefined,
        account_id: parseOptionalNumber(formData.account_id),
      });
      resetForm();
    } catch {
      setLocalError(t("transactions.bulkUpdateError", { defaultValue: "Could not update the selected transactions." }));
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={t("transactions.updateSelected")}
      subtitle={t("transactions.applyChanges", { count: selectedCount })}
      labelledBy="bulk-update-transactions-title"
      onSubmit={handleSubmit}
      maxWidthClassName="max-w-[720px]"
      disableClose={isSubmitting}
      footer={
        <ModalActions
          cancelLabel={t("common.actions.cancel")}
          submitLabel={t("transactions.updateSelectedCta", { defaultValue: "Update selected" })}
          submittingLabel={t("common.states.saving")}
          onCancel={handleClose}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!hasChanges}
        />
      }
    >
      <div className="space-y-4">
        {localError ? <MessageBanner tone="error">{localError}</MessageBanner> : null}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField label={t("common.labels.category")} htmlFor="bulk-update-category">
            <SelectInput
              id="bulk-update-category"
              value={formData.category_id}
              onChange={(event) => handleFieldChange("category_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange", { defaultValue: "No change" })}</option>
              {sortedCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={t("common.labels.tag")} htmlFor="bulk-update-tag">
            <SelectInput
              id="bulk-update-tag"
              value={formData.tag_id}
              onChange={(event) => handleFieldChange("tag_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange", { defaultValue: "No change" })}</option>
              {sortedTags.map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={t("common.labels.type")} htmlFor="bulk-update-type">
            <SelectInput
              id="bulk-update-type"
              value={formData.type_id}
              onChange={(event) => handleFieldChange("type_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange", { defaultValue: "No change" })}</option>
              {transactionTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={t("transactions.transactionDate", { defaultValue: "Transaction date" })} htmlFor="bulk-update-date">
            <TextInput
              id="bulk-update-date"
              type="date"
              value={formData.transaction_date}
              onChange={(event) => handleFieldChange("transaction_date", event.target.value)}
            />
          </FormField>

          <FormField label={t("transactions.installment")} htmlFor="bulk-update-installment">
            <SelectInput
              id="bulk-update-installment"
              value={formData.installment_id}
              onChange={(event) => handleFieldChange("installment_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange")}</option>
              {sortedInstallments.map((installment) => (
                <option key={installment.id} value={installment.id}>
                  {installment.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          <FormField label={t("transactions.savingPlan")} htmlFor="bulk-update-saving-plan">
            <SelectInput
              id="bulk-update-saving-plan"
              value={formData.saving_plan_id}
              onChange={(event) => handleFieldChange("saving_plan_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange")}</option>
              {sortedSavingPlans.map((savingPlan) => (
                <option key={savingPlan.id} value={savingPlan.id}>
                  {savingPlan.name}
                </option>
              ))}
            </SelectInput>
          </FormField>

          {FEATURE_FLAGS.savingPockets ? <FormField label={t("transactions.savingPocket")} htmlFor="bulk-update-saving-pocket">
            <SelectInput
              id="bulk-update-saving-pocket"
              value={formData.saving_pocket_id}
              onChange={(event) => handleFieldChange("saving_pocket_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange")}</option>
              {sortedSavingPocketOptions.map((savingPocket) => (
                <option key={savingPocket.id} value={savingPocket.id}>
                  {savingPocket.name}
                </option>
              ))}
            </SelectInput>
          </FormField> : null}

          <FormField label={t("transactions.account")} htmlFor="bulk-update-account" className="sm:col-span-2">
            <SelectInput
              id="bulk-update-account"
              value={formData.account_id}
              onChange={(event) => handleFieldChange("account_id", event.target.value)}
            >
              <option value="">{t("transactions.noChange")}</option>
              {sortedAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
        </div>
      </div>
    </ModalShell>
  );
};
